import { useState } from 'react';
import { Clapperboard, Eye, Layers, Plus } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { useStories } from '@/lib/queries';
import type { StoryListItem } from '@/lib/types';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { StoryEditorModal } from './StoryEditorModal';

/** Tray lifecycle filter. */
type StoryFilter = 'all' | 'published' | 'unpublished';
const MATCHES: Record<StoryFilter, (s: StoryListItem) => boolean> = {
  all: () => true,
  published: (s) => s.status === 'PUBLISHED',
  unpublished: (s) => s.status !== 'PUBLISHED',
};

/** Filter chips, in tray order. `dot` colours the status marker (none for All). */
const STATUS_FILTERS: Array<{
  value: StoryFilter;
  labelKey: 'filterAll' | 'filterPublished' | 'filterUnpublished';
  dot: string | null;
}> = [
  { value: 'all', labelKey: 'filterAll', dot: null },
  { value: 'published', labelKey: 'filterPublished', dot: 'bg-emerald-500' },
  { value: 'unpublished', labelKey: 'filterUnpublished', dot: 'bg-line-strong' },
];

/**
 * A story tray, not a card grid. These records ARE Instagram-style rings, so
 * the list renders them as rings: a gradient-bordered circle staff recognise
 * from the app itself, the label underneath, the slide count on the disc. The
 * ring's border carries state — a live gradient for a published, still-running
 * ring; a quiet hairline for a draft; a faded grey for one past its TTL — so a
 * marketer reads status at a glance without a table of badges.
 *
 * Order matches how the app will show them: live rings first, drafts next,
 * expired last (the "seen → end" instinct, applied to lifecycle).
 */
const RANK = (s: StoryListItem) => (s.isExpired ? 2 : s.status === 'PUBLISHED' ? 0 : 1);

const RING_ACCENTS = ['#2f4dec', '#0d9488', '#7c3aed', '#d97706', '#db2777', '#0284c7'];

export function Stories() {
  const { t, lang } = useI18n();
  const { data, isLoading } = useStories();
  const allRows = [...(data?.items ?? [])].sort((a, b) => RANK(a) - RANK(b) || a.order - b.order);
  // The ring editor opens in a modal over the tray — a story id, 'new', or null.
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [filter, setFilter] = useState<StoryFilter>('all');

  // Per-status counts drive the badge on each filter chip (computed from the
  // full set, so a chip's badge is stable regardless of the active filter).
  const counts = {
    all: allRows.length,
    published: allRows.filter(MATCHES.published).length,
    unpublished: allRows.filter(MATCHES.unpublished).length,
  };
  const rows = allRows.filter(MATCHES[filter]);

  return (
    <>
      <PageHeader
        title={t.stories.title}
        subtitle={t.stories.subtitle}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
            {t.stories.new}
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-content px-4 pb-12 sm:px-6">
        {isLoading ? (
          <RingGridSkeleton />
        ) : allRows.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={<Clapperboard className="h-6 w-6" />}
              title={t.stories.emptyTitle}
              hint={t.stories.emptyHint}
              action={
                <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
                  {t.stories.emptyAction}
                </Button>
              }
            />
          </div>
        ) : (
          <>
            {/* Lifecycle filter — a status-aware pill bar: a coloured dot per
                status (green = published, grey = draft), the label, and a live
                count that brightens on the active chip. */}
            <div className="mb-6 inline-flex items-center gap-1 rounded-full border border-line bg-raised p-1">
              {STATUS_FILTERS.map((f) => {
                const active = filter === f.value;
                return (
                  <button
                    key={f.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFilter(f.value)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-full px-3.5 py-1.5 text-ui font-medium',
                      'transition-ui duration-instant ease-out',
                      active
                        ? 'bg-surface text-strong shadow-seg ring-1 ring-line-strong'
                        : 'text-muted hover:text-strong',
                    )}
                  >
                    {f.dot && (
                      <span className={cn('h-1.5 w-1.5 rounded-full', f.dot, !active && 'opacity-50')} />
                    )}
                    {t.stories[f.labelKey]}
                    <span
                      className={cn(
                        'min-w-[1.25rem] rounded-chip px-1.5 text-center text-[0.6875rem] font-semibold tabular-nums',
                        active ? 'bg-brand-500/14 text-brand-700 dark:text-brand-200' : 'bg-line/50 text-faint',
                      )}
                    >
                      {counts[f.value]}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-3 gap-x-4 gap-y-7 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7">
              {/* One-tap create, framed as a ring so it reads as "add a story". */}
              <AddRing label={t.stories.new} onClick={() => setEditing('new')} />
              {rows.map((story, i) => (
                <StoryRing
                  key={story.id}
                  story={story}
                  accent={RING_ACCENTS[i % RING_ACCENTS.length]!}
                  title={story.title[lang] ?? story.title.uz ?? t.articles.untitled}
                  onClick={() => setEditing(story.id)}
                />
              ))}
            </div>

            {/* The active filter matched nothing (but other statuses have
                stories) — a quiet inline note, not a full empty-state. */}
            {rows.length === 0 && (
              <p className="mt-2 text-ui text-muted">{t.stories.filterEmpty}</p>
            )}
          </>
        )}
      </div>

      <StoryEditorModal storyId={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function StoryRing({
  story, accent, title, onClick,
}: {
  story: StoryListItem;
  accent: string;
  title: string;
  onClick: () => void;
}) {
  const { t } = useI18n();
  const live = story.status === 'PUBLISHED' && !story.isExpired;
  const cover = story.cover?.url ?? null;

  // The ring border is the status. A live ring gets the brand story-gradient; a
  // draft a quiet hairline; an expired one a faded grey (its image dims to match).
  const ringStyle = live
    ? { background: 'linear-gradient(135deg,#2f4dec 0%,#7c3aed 52%,#ed1f3a 100%)' }
    : undefined;
  const ringClass = live ? '' : story.isExpired ? 'bg-faint/50' : 'bg-line-strong';

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="group flex w-full cursor-pointer flex-col items-center gap-3 rounded-card p-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
    >
      <span className="relative">
        <span
          className={cn('block rounded-full p-[3px] transition-transform duration-quick ease-out group-hover:scale-[1.05]', ringClass)}
          style={ringStyle}
        >
          <span className="block rounded-full bg-surface p-[3px]">
            <span className="relative block h-[92px] w-[92px] overflow-hidden rounded-full sm:h-[104px] sm:w-[104px]">
              {cover ? (
                <img
                  src={cover}
                  alt=""
                  loading="lazy"
                  className={cn('h-full w-full object-cover', story.isExpired && 'opacity-70 grayscale')}
                />
              ) : (
                <span
                  className="flex h-full w-full items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${accent}, ${accent}c0)` }}
                >
                  <Clapperboard aria-hidden className="h-8 w-8 text-white/95" />
                </span>
              )}
            </span>
          </span>
        </span>

        {/* Slide count, on the disc like an unread-count. */}
        <span className="absolute bottom-0.5 right-0.5 inline-flex items-center gap-1 rounded-chip bg-surface px-1.5 py-0.5 text-[0.6875rem] font-semibold text-strong shadow-card ring-1 ring-line">
          <Layers aria-hidden className="h-3 w-3 text-faint" />
          {story.slideCount}
        </span>

        {live && (
          <span aria-hidden className="absolute right-1.5 top-1.5 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-surface" />
        )}
      </span>

      <span className="flex min-w-0 flex-col items-center gap-0.5 text-center">
        <span className="line-clamp-1 max-w-[7.5rem] text-ui font-semibold text-strong">{title}</span>
        <span className="inline-flex items-center gap-1 text-[0.6875rem] font-medium">
          {story.isExpired ? (
            <span className="text-accent-600 dark:text-accent-400">{t.stories.expired}</span>
          ) : live ? (
            <span className="inline-flex items-center gap-1 text-faint">
              <Eye aria-hidden className="h-3 w-3" />
              {story.viewCount}
            </span>
          ) : (
            <span className="text-muted">{t.stories.statusDraft}</span>
          )}
        </span>
      </span>
    </button>
  );
}

function AddRing({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full cursor-pointer flex-col items-center gap-3 rounded-card p-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
    >
      <span className="flex h-[100px] w-[100px] items-center justify-center rounded-full border-2 border-dashed border-line-strong text-faint transition-ui duration-instant group-hover:border-brand-500 group-hover:text-brand-500 sm:h-[112px] sm:w-[112px]">
        <Plus aria-hidden className="h-8 w-8" strokeWidth={1.75} />
      </span>
      <span className="text-ui font-semibold text-muted transition-colors group-hover:text-strong">{label}</span>
    </button>
  );
}

/** Ring-shaped placeholders while the tray loads. */
function RingGridSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-x-4 gap-y-7 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7" aria-busy>
      {Array.from({ length: 12 }, (_, i) => (
        <div key={i} className="flex flex-col items-center gap-3 p-1">
          <Skeleton className="h-[100px] w-[100px] rounded-full sm:h-[112px] sm:w-[112px]" />
          <Skeleton className="h-3.5 w-16" />
        </div>
      ))}
    </div>
  );
}
